import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import _ from 'lodash';
import superadminServices from 'app/services/superadmin-services';
import { SaveButton } from 'app/shared/ui';
import { describeError } from './visionDisplay';

const CODE_FORMAT = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/;
const EMPTY_FORM = { adoption_code: '', company_id: '', store_id: '', name: '' };

// Adopta un equipo de Xpit AI Vision: por selección (device) o por el código que muestra en su pantalla. El equipo nunca
// sabe su empresa ni su local; aquí se eligen, y Xpit Connect genera la contraseña de su interfaz.
const AdoptVisionDeviceDialog = ({ open, onClose, device = null, companies = [], onAdopted }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [stores, setStores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [adopted, setAdopted] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setAdopted(null);
    setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open || !form.company_id) {
      setStores([]);
      return;
    }
    superadminServices.getDeviceEditOptions({ companyId: form.company_id })
      .then((resp) => setStores(resp?.stores || []))
      .catch(() => setStores([]));
  }, [open, form.company_id]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const code = form.adoption_code.toUpperCase().replace(/[\s-]/g, '');

  const submit = async () => {
    if (!device && !CODE_FORMAT.test(code)) {
      setError('El código tiene 6 caracteres, sin I, O, 0 ni 1.');
      return;
    }
    if (!form.store_id) {
      setError('Elige la empresa y el local.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await superadminServices.adoptVisionDevice({
        ...(device ? { id: device.id } : { adoption_code: code }),
        store_id: form.store_id,
        name: form.name.trim() || null,
      });
      setAdopted(result);
      onAdopted?.(result);
    } catch (requestError) {
      setError(describeError(requestError, 'No se pudo adoptar el equipo'));
    } finally {
      setSaving(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(adopted.ui_password);
      setCopied(true);
    } catch (copyError) {
      setCopied(false);
    }
  };

  const selectedCompany = _.find(companies, (company) => `${company.id}` === `${form.company_id}`) || null;
  const selectedStore = _.find(stores, (store) => `${store.id}` === `${form.store_id}`) || null;
  const adoptedDevice = adopted?.vision_device;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{device ? `Adoptar ${device.device_id}` : 'Adoptar por código'}</DialogTitle>
      <DialogContent>
        {adoptedDevice ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {adoptedDevice.name} quedó en {adoptedDevice.company?.name} · {adoptedDevice.store?.name} con{' '}
              {adoptedDevice.points_count} {adoptedDevice.points_count === 1 ? 'punto' : 'puntos'}. Desde ahora se guarda su telemetría.
            </Alert>
            <Typography variant="subtitle2" color="text.secondary">Contraseña de la interfaz del equipo</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 1 }}>
              <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>{adopted.ui_password}</Typography>
              <Tooltip title={copied ? 'Copiada' : 'Copiar'}>
                <IconButton onClick={copyPassword} size="small"><ContentCopyOutlinedIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Llega al equipo por AWS y desde ahí sirve para entrar a su interfaz ({adoptedDevice.ui_url}). Se puede volver a ver
              o rotar en el detalle del equipo.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            {device ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  MAC {device.mac || '-'} · código {device.adoption_code || '-'} · IP {(device.local_ips || []).join(', ') || '-'}
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  autoFocus
                  label="Código de adopción"
                  helperText="El que muestra el equipo en su pantalla, 6 caracteres."
                  value={form.adoption_code}
                  inputProps={{ maxLength: 7, style: { fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 2 } }}
                  onChange={(e) => updateForm({ adoption_code: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={companies}
                value={selectedCompany}
                getOptionLabel={(option) => option?.name || ''}
                onChange={(e, value) => updateForm({ company_id: value?.id || '', store_id: '' })}
                renderInput={(params) => <TextField {...params} required label="Empresa" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={stores}
                value={selectedStore}
                getOptionLabel={(option) => option?.name || ''}
                onChange={(e, value) => updateForm({ store_id: value?.id || '' })}
                renderInput={(params) => <TextField {...params} required label="Local" helperText="Ahí se crean sus puntos." />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del equipo (opcional)"
                helperText="Los puntos toman el nombre de cada analítica configurada en el equipo."
                value={form.name}
                onChange={(e) => updateForm({ name: e.target.value })}
              />
            </Grid>
          </Grid>
        )}
        {!!error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{adoptedDevice ? 'Cerrar' : 'Cancelar'}</Button>
        {!adoptedDevice && (
          <SaveButton loading={saving} disabled={saving} onClick={submit}>Adoptar</SaveButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdoptVisionDeviceDialog;
